import React, { useState, useEffect } from 'react'
import Dropdown from 'particles/Dropdown'
import { useNewTranslation } from 'hooks/index'
import { BaseInputFieldProps } from '.'
import { RangeInputContainer } from './style'
import Text from 'particles/Text'
import MenuItem from 'particles/MenuItem'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import CommentContainer from 'components/Shared/Comment'
import Label from 'particles/Label'
import IconButton from 'particles/Button/IconButton'

export interface OptionsState {
    label: string
    value: any
    isDisabled?: boolean
    tooltipContext?: {
        label: string
        title: string
    }
}

interface DropdownRangeProps<T = unknown> extends BaseInputFieldProps {
    firstRangeOnChange: (value: T) => void
    secondRangeOnChange: (value: T) => void
    firstRangeValue?: T
    secondRangeValue?: T
    firstRangeOptions: OptionsState[]
    secondRangeOptions: OptionsState[]
    isSearchable?: boolean
}

const DropdownRange: React.FC<DropdownRangeProps> = ({
    label,
    subtext,
    size = 'm',
    firstRangeTitle,
    firstRangeName,
    firstRangePlaceholder,
    firstRangeIdentifier,
    secondRangeTitle,
    secondRangeName,
    secondRangePlaceholder,
    secondRangeIdentifier,
    isDisabled = false,
    width,
    inputWidth,
    isReadOnly = false,
    isVertical = false,
    isRequired,
    isCommentable,
    isCommented,
    firstRangeOnChange,
    secondRangeOnChange,
    firstRangeValue,
    secondRangeValue,
    firstRangeId,
    secondRangeId,
    isSearchable,
    errorMessage,
    onClose,
    className,
    identifier,
    ...props
}) => {
    const [newT] = useNewTranslation()

    const [firstRangeOptions, setFirstRangeOptions] = useState(props.firstRangeOptions)
    const [secondRangeOptions, setSecondRangeOptions] = useState(props.secondRangeOptions)

    useEffect(() => {
        setFirstRangeOptions(props.firstRangeOptions)
        setSecondRangeOptions(props.secondRangeOptions)
    }, [props.firstRangeOptions, props.secondRangeOptions])

    const handleSearch = (value: string, isFirstRange?: boolean) => {
        const newFirstRangeOptions = props.firstRangeOptions ? [...props.firstRangeOptions] : []
        const newSecondRangeOptions = props.secondRangeOptions ? [...props.secondRangeOptions] : []
        if (isFirstRange) {
            setFirstRangeOptions(
                newFirstRangeOptions.filter(item => item.label.toLowerCase().includes(value.toLowerCase())),
            )
            return
        }
        setSecondRangeOptions(
            newSecondRangeOptions.filter(item => item.label.toLowerCase().includes(value.toLowerCase())),
        )
    }
    const handleClearSearch = () => {
        setFirstRangeOptions(props.firstRangeOptions)
        setSecondRangeOptions(props.secondRangeOptions)
    }

    return (
        <RangeInputContainer className={className} isVertical={isVertical} width={width}>
            {(label || subtext) && (
                <div style={{ display: 'flex' }}>
                    <CommentContainer identifier={identifier}>
                        <Label
                            title={label ?? ''}
                            subtext={subtext}
                            isRequired={isRequired}
                            size={size}
                            isCommentable={isCommentable}
                            isCommented={isCommented}
                        />
                    </CommentContainer>
                    {onClose && !isReadOnly ? (
                        <IconButton icon="x" form="icon" size="xs" color="neutral" onClick={onClose} />
                    ) : null}
                </div>
            )}
            <div className="range-input">
                <Dropdown
                    label={firstRangeTitle}
                    placeholder={firstRangePlaceholder}
                    isDisabled={isDisabled}
                    width={inputWidth}
                    isReadOnly={isReadOnly}
                    value={firstRangeValue}
                    onChange={(value): void => firstRangeOnChange(value)}
                    size={size}
                    id={firstRangeId}
                    onClose={handleClearSearch}
                    className="range-input-item"
                    identifier={firstRangeIdentifier}
                >
                    {isSearchable && (
                        <MenuItem.Search
                            onSearch={value => handleSearch(value, true)}
                            onClearSearch={handleClearSearch}
                        />
                    )}
                    {firstRangeOptions?.length > 0 ? (
                        firstRangeOptions.map((item: OptionsState, index: number) => (
                            <MenuItem.Text
                                key={`menu-item-${index}`}
                                tabIndex={index}
                                text={item.label}
                                isDisabled={item.isDisabled}
                                tooltipContext={item.tooltipContext}
                                value={item.value}
                            />
                        ))
                    ) : (
                        <EmptySearchResult />
                    )}
                </Dropdown>
                <Text scale="bodyS500" className="to-block">
                    {newT('general.information.toRangeFields')}
                </Text>
                <Dropdown
                    label={secondRangeTitle}
                    placeholder={secondRangePlaceholder}
                    isDisabled={isDisabled}
                    width={inputWidth}
                    isReadOnly={isReadOnly}
                    value={secondRangeValue}
                    onChange={(value): void => secondRangeOnChange(value)}
                    size={size}
                    id={secondRangeId}
                    onClose={handleClearSearch}
                    className="range-input-item"
                    identifier={secondRangeIdentifier}
                >
                    {isSearchable && <MenuItem.Search onSearch={handleSearch} onClearSearch={handleClearSearch} />}
                    {secondRangeOptions?.length > 0 ? (
                        secondRangeOptions.map((item: OptionsState, index: number) => (
                            <MenuItem.Text
                                key={`menu-item-${index}`}
                                tabIndex={index}
                                text={item.label}
                                isDisabled={item.isDisabled}
                                tooltipContext={item.tooltipContext}
                                value={item.value}
                            />
                        ))
                    ) : (
                        <EmptySearchResult />
                    )}
                </Dropdown>
            </div>
            {!isDisabled && errorMessage && (
                <Text scale="caption400" color="error">
                    {errorMessage}
                </Text>
            )}
        </RangeInputContainer>
    )
}
export default DropdownRange
